/**
 * 資格拡張エンジン 生成問題パネル（design.md §6b / tasklist L-6）
 *
 * AdminPage の "generated" タブ本体。2つのビューを持つ:
 * - 一覧ビュー（監査証跡）: 区分・ゾーン・状態で絞り込み、開くと7ゲート+較正/二次レビュー所見を読める
 * - 較正ビュー（初回較正専用）: 層別サンプルを1問ずつ表示し O(問題なし)/D(欠陥あり)をキーボードで記録する
 *
 * 人手レビューは定常運用に無い設計（design.md §1）。このパネルは「監査証跡の閲覧」と
 * 「β公開前の初回較正1回」のためだけに存在し、1問ずつ承認して回す作りにはしない。
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  ChevronDown,
  ChevronUp,
  Loader2,
  AlertCircle,
  ClipboardCheck,
  ListChecks,
  Check,
  X,
} from "lucide-react";
import { useAuth } from "../../../contexts/AuthContext";
import { apiGet, apiPost } from "../../../api/client";

const CHOICE_LABELS = ["ア", "イ", "ウ", "エ"];

// --- 型（lambda/admin-review/index.js の応答形と一致させる） ---
export interface GateResult {
  verdict: string;
  [key: string]: unknown;
}
export type GateResults = Record<string, GateResult>;

export interface GeneratedQuestion {
  id: string;
  exam_code: string;
  section: string;
  topic_id: string;
  syllabus_ver: string;
  zone: string;
  batch_id: string;
  status: string;
  question_text: string;
  choices: string[];
  correct_answer: number;
  choice_rationales: string[];
  grounding_terms: string[] | null;
  difficulty: string | null;
  gate_results: GateResults;
  review_note: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  calibration_verdict: "ok" | "defect" | null;
  calibration_note: string | null;
  created_at: string;
}

// lambda/admin-review/index.js の VALID_STATUSES/VALID_ZONES と同期を保つこと
const STATUSES = [
  "generated", "gated_pass", "gated_fail", "quality_pass", "quality_fail",
  "pending", "approved", "rejected", "published", "stale",
] as const;
const ZONES = ["new", "reuse", "unknown"] as const;

// 07_gate_summary.py の GATES と同一・要同期維持
export const GATE_ORDER = [
  "consensus", "grounding", "logic", "similarity", "standards",
  "rationale_effectiveness", "clarity",
] as const;

const GATE_LABELS: Record<string, string> = {
  consensus: "合議",
  grounding: "グラウンディング",
  logic: "論理整合",
  similarity: "翻案・重複",
  standards: "規格整合",
  rationale_effectiveness: "根拠有効性",
  clarity: "わかりやすさ",
};

const VERDICT_STYLE: Record<string, { color: string; label: string }> = {
  pass: { color: "text-green-600 dark:text-green-400", label: "✅" },
  fail: { color: "text-red-600 dark:text-red-400", label: "❌" },
  review: { color: "text-amber-600 dark:text-amber-400", label: "⚠️" },
  pending: { color: "text-slate-400 dark:text-slate-500", label: "…" },
  skip: { color: "text-slate-400 dark:text-slate-500", label: "—" },
  not_run: { color: "text-slate-400 dark:text-slate-500", label: "—" },
};

/** verdict文字列（"fail_wrong_key"等の亜種を含む）からスタイルを引く。 */
export function verdictStyle(v: string | undefined): { color: string; label: string } {
  if (!v) return VERDICT_STYLE.not_run;
  if (v.startsWith("fail")) return VERDICT_STYLE.fail;
  return VERDICT_STYLE[v] || VERDICT_STYLE.not_run;
}

/** 1ゲートの結果を1行の日本語サマリに変換する。フィールドが無ければ「未実行」。 */
export function describeGate(name: string, r: GateResult | undefined): string {
  if (!r) return "未実行";
  switch (name) {
    case "consensus": {
      const votes = (r.votes as Record<string, unknown>) || {};
      const n = Object.keys(votes).length;
      return n > 0 ? `${n}社応答・正解キー${r.correctAnswer}` : "応答なし";
    }
    case "grounding": {
      const pts = (r.ungrounded_points as string[]) || [];
      return pts.length ? `範囲外の指摘${pts.length}件` : "用語例の範囲内";
    }
    case "logic": {
      const issues = (r.issues as string[]) || [];
      return `一貫性:${r.consistency ?? "-"}${issues.length ? `・指摘${issues.length}件` : ""}`;
    }
    case "similarity": {
      const top = (r.top_matches as { id: string; sim: number }[] | undefined)?.[0];
      return top ? `最大類似度${r.max_similarity}（${top.id}）` : `最大類似度${r.max_similarity ?? "-"}`;
    }
    case "standards": {
      const v = (r.violations as string[]) || [];
      return v.length ? `違反の指摘${v.length}件` : "矛盾なし";
    }
    case "rationale_effectiveness": {
      return r.predicted === r.correctAnswer
        ? "根拠のみで正解に到達"
        : `根拠のみでは不到達（予測${r.predicted}）`;
    }
    case "clarity": {
      return `${r.total ?? "-"}/25点`;
    }
    default:
      return "";
  }
}

// ============================================================
// 一覧ビュー（監査証跡）
// ============================================================

const GeneratedQuestionRow: React.FC<{ q: GeneratedQuestion }> = ({ q }) => {
  const [expanded, setExpanded] = useState(false);
  const correctLabel =
    q.correct_answer >= 0 && q.correct_answer < CHOICE_LABELS.length
      ? CHOICE_LABELS[q.correct_answer]
      : "?";

  return (
    <div className="border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-slate-50 dark:hover:bg-slate-900 transition-colors"
      >
        <span className="text-xs font-mono text-slate-500 dark:text-slate-400 w-56 shrink-0 truncate">
          {q.id}
        </span>
        <span className="text-xs font-bold text-slate-700 dark:text-slate-300 w-14 shrink-0">
          {q.exam_code}
        </span>
        <span className="text-xs text-slate-500 dark:text-slate-400 w-16 shrink-0">{q.zone}</span>
        <span className="text-sm text-slate-900 dark:text-slate-100 flex-1 truncate">
          {q.question_text}
        </span>
        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 shrink-0">
          {q.status}
        </span>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-slate-500 dark:text-slate-400 shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-slate-500 dark:text-slate-400 shrink-0" />
        )}
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-slate-200 dark:border-slate-600 pt-3 space-y-3">
          <div>
            <h4 className="text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">問題文</h4>
            <p className="text-sm text-slate-900 dark:text-slate-100 whitespace-pre-wrap">
              {q.question_text}
            </p>
          </div>

          <div>
            <h4 className="text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">選択肢</h4>
            <div className="space-y-1">
              {q.choices.map((c, i) => (
                <div
                  key={i}
                  className={`text-sm px-2 py-1 rounded ${
                    i === q.correct_answer
                      ? "bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300 font-medium border border-green-200 dark:border-green-800"
                      : "text-slate-700 dark:text-slate-300"
                  }`}
                >
                  <span className="font-bold mr-2">{CHOICE_LABELS[i] || i + 1}</span>
                  {c}
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">正解: {correctLabel}</p>
          </div>

          <div>
            <h4 className="text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">
              7ゲート（design.md §4a/§4e）
            </h4>
            <div className="space-y-0.5">
              {GATE_ORDER.map((g) => {
                const r = q.gate_results?.[g];
                const style = verdictStyle(r?.verdict);
                return (
                  <div key={g} className="text-xs flex items-center gap-2">
                    <span className={`${style.color} w-5 shrink-0`}>{style.label}</span>
                    <span className="text-slate-500 dark:text-slate-400 w-24 shrink-0">
                      {GATE_LABELS[g]}
                    </span>
                    <span className="text-slate-700 dark:text-slate-300">{describeGate(g, r)}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {(q.review_note || q.reviewed_by) && (
            <div>
              <h4 className="text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">
                二次レビュー所見（{q.reviewed_by || "不明"}）
              </h4>
              <p className="text-sm text-slate-700 dark:text-slate-300">{q.review_note || "（差戻し理由なし）"}</p>
            </div>
          )}

          {q.calibration_verdict && (
            <div>
              <h4 className="text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">初回較正</h4>
              <p
                className={`text-sm ${
                  q.calibration_verdict === "ok"
                    ? "text-green-700 dark:text-green-400"
                    : "text-red-600 dark:text-red-400"
                }`}
              >
                {q.calibration_verdict === "ok" ? "問題なし" : `欠陥あり: ${q.calibration_note || ""}`}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const GeneratedListView: React.FC = () => {
  const { user } = useAuth();
  const [items, setItems] = useState<GeneratedQuestion[]>([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState<string>("");
  const [examCode, setExamCode] = useState<string>("");
  const [zone, setZone] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchList = useCallback(async () => {
    if (!user?.idToken) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number> = { limit: 50 };
      if (status) params.status = status;
      if (examCode) params.exam_code = examCode;
      if (zone) params.zone = zone;
      const data = await apiGet<{ items: GeneratedQuestion[]; total: number }>(
        "/admin/review/questions",
        params,
        user.idToken
      );
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : "取得に失敗しました");
    } finally {
      setIsLoading(false);
    }
  }, [user?.idToken, status, examCode, zone]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-600 p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1">
            <span className="text-xs text-slate-500 dark:text-slate-400 mr-1">状態</span>
            <button
              onClick={() => setStatus("")}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                !status
                  ? "bg-slate-900 dark:bg-slate-700 text-white dark:text-slate-100"
                  : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600"
              }`}
            >
              すべて
            </button>
            {STATUSES.map((s) => (
              <button
                key={s}
                onClick={() => setStatus(status === s ? "" : s)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  status === s
                    ? "bg-slate-900 dark:bg-slate-700 text-white dark:text-slate-100"
                    : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          <div className="hidden sm:block w-px h-5 bg-slate-200 dark:bg-slate-600" />
          <div className="flex items-center gap-1">
            <span className="text-xs text-slate-500 dark:text-slate-400 mr-1">ゾーン</span>
            <button
              onClick={() => setZone("")}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                !zone
                  ? "bg-slate-900 dark:bg-slate-700 text-white dark:text-slate-100"
                  : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600"
              }`}
            >
              すべて
            </button>
            {ZONES.map((z) => (
              <button
                key={z}
                onClick={() => setZone(zone === z ? "" : z)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  zone === z
                    ? "bg-slate-900 dark:bg-slate-700 text-white dark:text-slate-100"
                    : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600"
                }`}
              >
                {z}
              </button>
            ))}
          </div>
          <div className="hidden sm:block w-px h-5 bg-slate-200 dark:bg-slate-600" />
          <div className="flex items-center gap-1">
            <span className="text-xs text-slate-500 dark:text-slate-400 mr-1">区分</span>
            <input
              type="text"
              value={examCode}
              onChange={(e) => setExamCode(e.target.value.toUpperCase())}
              placeholder="PD-M等"
              className="w-24 px-2 py-1.5 text-xs rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
            />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">監査証跡</h3>
        <span className="text-sm text-slate-500 dark:text-slate-400">
          全 <span className="font-bold text-slate-900 dark:text-slate-100">{total}</span> 問
        </span>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
        </div>
      ) : error ? (
        <div className="text-center py-12">
          <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
          <p className="text-sm text-slate-500 dark:text-slate-400">{error}</p>
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-slate-400 dark:text-slate-500 text-sm">
          該当する生成問題がありません
        </div>
      ) : (
        <div className="space-y-1">
          {items.map((q) => (
            <GeneratedQuestionRow key={q.id} q={q} />
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================
// 較正ビュー（初回較正専用・design.md §6b/§6c）
// ============================================================

const DEFECT_REASONS: { key: string; label: string; fatal: boolean }[] = [
  { key: "wrong_answer", label: "正解誤り", fatal: true },
  { key: "multiple_correct", label: "複数正解", fatal: true },
  { key: "out_of_scope", label: "範囲外", fatal: true },
  { key: "standards_violation", label: "規格違反", fatal: true },
  { key: "notation", label: "表記", fatal: false },
  { key: "unclear", label: "わかりにくい", fatal: false },
];

interface CalibrationRecord {
  id: string;
  verdict: "ok" | "defect";
  reason?: string;
  fatal?: boolean;
}

/** 較正結果からdesign.md §6cの判定（スキーム承認/公開停止）を出す。 */
export function summarizeCalibration(records: CalibrationRecord[]): {
  fatalCount: number;
  approved: boolean;
} {
  const fatalCount = records.filter((r) => r.verdict === "defect" && r.fatal).length;
  return { fatalCount, approved: fatalCount === 0 };
}

const CalibrationView: React.FC = () => {
  const { user } = useAuth();
  const [sample, setSample] = useState<GeneratedQuestion[] | null>(null);
  const [index, setIndex] = useState(0);
  const [records, setRecords] = useState<CalibrationRecord[]>([]);
  const [pickingReason, setPickingReason] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (!user?.idToken) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    apiGet<{ items: GeneratedQuestion[] }>("/admin/review/calibration", { count: 30 }, user.idToken)
      .then((data) => setSample(data.items))
      .catch((e) => setError(e instanceof Error ? e.message : "取得に失敗しました"))
      .finally(() => setIsLoading(false));
  }, [user?.idToken]);

  const current = sample && index < sample.length ? sample[index] : null;

  const record = useCallback(
    async (verdict: "ok" | "defect", reason?: string, fatal?: boolean) => {
      if (!current || submittingRef.current || !user?.idToken) return;
      submittingRef.current = true;
      setSubmitting(true);
      try {
        await apiPost(`/admin/review/${current.id}/verdict`, { verdict, reason }, user.idToken);
        setRecords((prev) => [...prev, { id: current.id, verdict, reason, fatal }]);
        setIndex((i) => i + 1);
        setPickingReason(false);
      } catch (e) {
        setError(e instanceof Error ? e.message : "記録に失敗しました");
      } finally {
        submittingRef.current = false;
        setSubmitting(false);
      }
    },
    [current, user?.idToken]
  );

  useEffect(() => {
    if (!current || submitting) return;
    const onKeyDown = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      if (pickingReason) {
        const n = parseInt(key, 10);
        if (n >= 1 && n <= DEFECT_REASONS.length) {
          const r = DEFECT_REASONS[n - 1];
          record("defect", r.label, r.fatal);
        } else if (key === "escape") {
          setPickingReason(false);
        }
        return;
      }
      if (key === "o") record("ok");
      else if (key === "d") setPickingReason(true);
      else if (key === "j") setIndex((i) => Math.min(i + 1, (sample?.length ?? 1) - 1));
      else if (key === "k") setIndex((i) => Math.max(i - 1, 0));
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [current, submitting, pickingReason, record, sample?.length]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
        <p className="text-sm text-slate-500 dark:text-slate-400">{error}</p>
      </div>
    );
  }

  if (!sample || sample.length === 0) {
    return (
      <div className="text-center py-12 text-slate-400 dark:text-slate-500 text-sm">
        較正対象（quality_pass以降・未較正）の問題がありません
      </div>
    );
  }

  // 完了画面（design.md §6c）
  if (!current) {
    const { fatalCount, approved } = summarizeCalibration(records);
    const ok = records.filter((r) => r.verdict === "ok").length;
    return (
      <div className="max-w-lg mx-auto text-center py-8 space-y-4">
        <ClipboardCheck className="w-10 h-10 mx-auto text-slate-400" />
        <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">較正完了</h3>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {records.length}問中 問題なし {ok}問 ／ 欠陥あり {records.length - ok}問（うち致命的 {fatalCount}件）
        </p>
        <div
          className={`px-4 py-3 rounded-xl text-sm font-medium ${
            approved
              ? "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800"
              : "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800"
          }`}
        >
          {approved
            ? "致命的欠陥0件 — スキーム承認。以降は自動運用へ移行できます"
            : "致命的欠陥1件以上 — β公開を止め、ゲート/二次レビューを見直してから再較正してください"}
        </div>
      </div>
    );
  }

  const correctLabel = CHOICE_LABELS[current.correct_answer] || "?";

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">初回較正</h3>
        <span className="text-sm text-slate-500 dark:text-slate-400">
          {index + 1} / {sample.length}問
        </span>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-600 p-4 shadow-sm space-y-3">
        <p className="text-sm text-slate-900 dark:text-slate-100 whitespace-pre-wrap">
          {current.question_text}
        </p>
        <div className="space-y-1">
          {current.choices.map((c, i) => (
            <div
              key={i}
              className={`text-sm px-2 py-1 rounded ${
                i === current.correct_answer
                  ? "bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300 font-medium border border-green-200 dark:border-green-800"
                  : "text-slate-700 dark:text-slate-300"
              }`}
            >
              <span className="font-bold mr-2">{CHOICE_LABELS[i] || i + 1}</span>
              {c}
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400">正解: {correctLabel}</p>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-600 p-4 shadow-sm space-y-1">
        {GATE_ORDER.map((g) => {
          const r = current.gate_results?.[g];
          const style = verdictStyle(r?.verdict);
          return (
            <div key={g} className="text-xs flex items-center gap-2">
              <span className={`${style.color} w-5 shrink-0`}>{style.label}</span>
              <span className="text-slate-500 dark:text-slate-400 w-24 shrink-0">{GATE_LABELS[g]}</span>
              <span className="text-slate-700 dark:text-slate-300">{describeGate(g, r)}</span>
            </div>
          );
        })}
        {current.review_note && (
          <div className="text-xs flex items-start gap-2 pt-1 mt-1 border-t border-slate-100 dark:border-slate-700">
            <span className="text-slate-500 dark:text-slate-400 w-24 shrink-0">二次レビュー</span>
            <span className="text-slate-700 dark:text-slate-300">{current.review_note}</span>
          </div>
        )}
      </div>

      {pickingReason ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-red-200 dark:border-red-800 p-4 shadow-sm">
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">欠陥の理由を選択（数字キーでも可）</p>
          <div className="flex flex-wrap gap-2">
            {DEFECT_REASONS.map((r, i) => (
              <button
                key={r.key}
                disabled={submitting}
                onClick={() => record("defect", r.label, r.fatal)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors disabled:opacity-50 ${
                  r.fatal
                    ? "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30"
                    : "bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/30"
                }`}
              >
                {i + 1}. {r.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => setPickingReason(false)}
            className="mt-2 text-xs text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300"
          >
            キャンセル（Esc）
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-center gap-3">
          <button
            disabled={submitting}
            onClick={() => record("ok")}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition-colors"
          >
            <Check className="w-4 h-4" />
            [O] 問題なし
          </button>
          <button
            disabled={submitting}
            onClick={() => setPickingReason(true)}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition-colors"
          >
            <X className="w-4 h-4" />
            [D] 欠陥あり
          </button>
          <span className="text-xs text-slate-400 dark:text-slate-500">[J/K] 移動</span>
        </div>
      )}
    </div>
  );
};

// ============================================================
// パネル本体
// ============================================================

type GeneratedView = "list" | "calibration";

export const GeneratedPanel: React.FC = () => {
  const [view, setView] = useState<GeneratedView>("list");

  return (
    <div className="space-y-4">
      <div className="flex gap-1 bg-slate-100 dark:bg-slate-700 p-1 rounded-xl w-fit">
        <button
          onClick={() => setView("list")}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            view === "list"
              ? "bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 shadow-sm"
              : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
          }`}
        >
          <ListChecks className="w-4 h-4" />
          監査証跡
        </button>
        <button
          onClick={() => setView("calibration")}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            view === "calibration"
              ? "bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 shadow-sm"
              : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
          }`}
        >
          <ClipboardCheck className="w-4 h-4" />
          初回較正
        </button>
      </div>

      {view === "list" ? <GeneratedListView /> : <CalibrationView />}
    </div>
  );
};

export default GeneratedPanel;
